import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import type { EquipmentItem } from '@mads/shared'
import { ConfirmDialog, ErrorNote, Loading } from '@/components/Feedback'
import { useEmptyTrash, usePurgeItem, useRestoreItem, useTrash } from '@/lib/queries'
import { useIsAdmin } from '@/lib/session'

function formatDeletedAt(iso: string | null): string {
  if (iso === null) return '—'

  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Either confirmation the page can be showing: one record, or the lot. */
type Pending = { kind: 'purge'; item: EquipmentItem } | { kind: 'empty' }

/**
 * Deleted kit, kept rather than destroyed (issue #8). Anyone can put something
 * back; destroying a record for good is admin-only, because soft delete already
 * means nobody loses anything by binning the wrong thing — the irreversible
 * step is the one worth gating (spec W-05).
 */
export function Trash() {
  const isAdmin = useIsAdmin()
  const { data: items, isPending, error } = useTrash()

  const restore = useRestoreItem()
  const purge = usePurgeItem()
  const empty = useEmptyTrash()

  const [confirming, setConfirming] = useState<Pending | null>(null)

  if (isPending) return <Loading what="Loading the trash" />
  if (error !== null) return <ErrorNote error={error} />

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 className="page-title">Trash</h1>
          <p className="page-lede">
            Deleted equipment is kept here rather than destroyed. Restoring puts a record back in
            the inventory exactly as it was.
          </p>
        </div>

        {isAdmin && items.length > 0 && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setConfirming({ kind: 'empty' })}
          >
            Empty trash
          </button>
        )}
      </div>

      <Link to="/inventory" className="btn btn-ghost" style={{ paddingLeft: 0 }}>
        &larr; Back to inventory
      </Link>

      <ErrorNote error={restore.error ?? purge.error ?? empty.error} />

      {items.length === 0 ? (
        <p className="empty-note" style={{ marginTop: 'var(--space-6)' }}>
          The trash is empty.
        </p>
      ) : (
        <div className="table-scroll" style={{ marginTop: 'var(--space-6)' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Location</th>
                <th className="numeric">Qty</th>
                <th>Deleted</th>
                <th>By</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  {/* Not a link: a trashed item has no detail page to open. */}
                  <td>{item.displayName}</td>
                  <td>{item.category}</td>
                  <td className="text-muted">{item.location?.name ?? '—'}</td>
                  <td className="numeric">{item.quantity}</td>
                  <td className="text-muted">{formatDeletedAt(item.deletedAt)}</td>
                  <td className="text-muted">{item.deletedBy ?? 'Unknown'}</td>
                  {/* The actions stay on one line; the table scrolls instead. */}
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div className="action-row" style={{ flexWrap: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={restore.isPending}
                        onClick={() => restore.mutate(item.id)}
                      >
                        Restore
                      </button>

                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => setConfirming({ kind: 'purge', item })}
                        >
                          Delete for good
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirming?.kind === 'purge' && (
        <ConfirmDialog
          title="Delete this record for good?"
          body={`"${confirming.item.displayName}" will be destroyed, along with its place on any past stocktake. This cannot be undone.`}
          confirmLabel="Delete for good"
          busy={purge.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            purge.mutate(confirming.item.id, { onSuccess: () => setConfirming(null) })
          }}
        />
      )}

      {confirming?.kind === 'empty' && (
        <ConfirmDialog
          title="Empty the trash?"
          body={
            items.length === 1
              ? 'The one record in the trash will be destroyed, along with its place on any past stocktake. This cannot be undone.'
              : `All ${items.length} records in the trash will be destroyed, along with their place on any past stocktake. This cannot be undone.`
          }
          confirmLabel="Empty trash"
          busy={empty.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            empty.mutate(undefined, { onSuccess: () => setConfirming(null) })
          }}
        />
      )}
    </>
  )
}
